import type { TrainingDocument } from '~/gql/documents/documents'
import { useQuery } from '@apollo/client/react'
import { FileText, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { GQL_TRAINING_DOCUMENTS } from '~/gql/documents/documents'
import { GQL_SESSIONS } from '~/gql/sessions/sessions'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_TIME_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import { TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'
import AcknowledgementsDialog from './AcknowledgementsDialog'
import DocumentEditDialog from './DocumentEditDialog'
import DocumentsTable from './DocumentsTable'
import DocumentUploadDialog from './DocumentUploadDialog'
import { useDocumentActions } from './use-document-actions'

type Opened = { kind: 'edit' | 'acks' | 'archive', document: TrainingDocument }

/**
 * Documents remis aux apprenants (#326), aux admins : règlement intérieur,
 * programme, conditions, livret d'accueil, et qui en a pris connaissance
 * (Qualiopi, indicateur 9).
 */
function DocumentsPage() {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const { data, loading, refetch } = useQuery(GQL_TRAINING_DOCUMENTS, { fetchPolicy: 'cache-and-network' })
  const { data: sessionsData } = useQuery(GQL_SESSIONS)
  const [adding, setAdding] = useState(false)
  const [opened, setOpened] = useState<Opened | null>(null)
  const actions = useDocumentActions(() => {
    setOpened(null)
    void refetch()
  })
  const documents = data?.trainingDocuments ?? []
  const sessions = sessionsData?.sessions ?? []
  const formatDate = (value: unknown) => formatSessionDate(value, i18n.language, timezone, DATE_TIME_WITH_YEAR)

  return (
    <div className="flex w-full flex-col gap-6">
      <p className="text-sm text-muted-foreground" {...tourAnchor('documents.intro')}>{t('documents.intro')}</p>
      <TableToolbar>
        <ToolbarActions>
          <Button onClick={() => setAdding(true)} {...tourAnchor('documents.new')}>
            <Plus className="mr-2 size-4" />
            {t('documents.new.button')}
          </Button>
        </ToolbarActions>
      </TableToolbar>
      <div {...tourAnchor('documents.list')}>
        {loading && !data
          ? <div className="flex justify-center p-10"><Spinner className="size-6" /></div>
          : documents.length === 0
            ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-muted-foreground">
                  <FileText className="size-8" />
                  <p className="text-sm">{t('documents.empty')}</p>
                </div>
              )
            : (
                <div className="overflow-x-auto rounded-lg border">
                  <DocumentsTable
                    documents={documents}
                    onDownload={document => void actions.download(document)}
                    onEdit={document => setOpened({ kind: 'edit', document })}
                    onAcknowledgements={document => setOpened({ kind: 'acks', document })}
                    onArchive={document => setOpened({ kind: 'archive', document })}
                  />
                </div>
              )}
      </div>

      {adding && <DocumentUploadDialog sessions={sessions} onCancel={() => setAdding(false)} onUploaded={() => { setAdding(false); void refetch() }} />}
      {opened?.kind === 'edit' && (
        <DocumentEditDialog document={opened.document} sessions={sessions} busy={actions.busy} onCancel={() => setOpened(null)} onSave={form => void actions.update(opened.document.id, form)} />
      )}
      {opened?.kind === 'acks' && <AcknowledgementsDialog document={opened.document} formatDate={formatDate} onClose={() => setOpened(null)} />}
      <ConfirmDialog
        open={opened?.kind === 'archive'}
        title={t('documents.archiveConfirm.title')}
        confirmLabel={t('documents.archive')}
        pending={actions.busy}
        onConfirm={() => opened && void actions.archive(opened.document.id)}
        onCancel={() => setOpened(null)}
      >
        {t('documents.archiveConfirm.body', { title: opened?.document.title ?? '' })}
      </ConfirmDialog>
    </div>
  )
}

export default DocumentsPage
